import { Moment } from 'moment';
import { ChatId } from '@/chat/models/ids';
import ChatType, { visitChatType } from '@/shared/constants/ChatType';
import MobilitySolution from '@/shared/models/MobilitySolution';
import User from '@/shared/models/User';
import { QrCode, VatNumber } from '@/shared/models/common';
import DossierStack from '@/tasks/models/DossierStack';
import { AppointmentState } from '../constants/AppointmentState';
import BarcodeType from '../constants/BarcodeType';
import AbstractDossier from './AbstractDossier';
import DossierWorkshopPlanning from './DossierWorkshopPlanning';
import TransportContainer from './TransportContainer';
import { TransportData } from './TransportDetails';
import { DossierSlaNote } from '../converters/dossierConverters';
import SlaNoteType from './SlaNoteType';

export interface AppointmentData {
  waitsOnPremise: boolean;
  mobilitySolution: MobilitySolution | null;
  transportIn: TransportData;
  transportOut: TransportData;
}

export default class Dossier extends AbstractDossier {
  vat: VatNumber | null = null;
  internalChatId: ChatId | null = null;
  externalChatId: ChatId | null = null;
  externalChatCounter = 0;
  internalChatCounter = 0;
  hasKeyboxAvailable = false;
  mobilitySolution: MobilitySolution | null = null;
  workshopPlannings: Array<DossierWorkshopPlanning> = [];
  isLeasing = false;
  dmsBarcodePrefix: string | null = null;
  dmsBarcodeType: BarcodeType | null = null;
  dmsConnectionName: string | null = null;
  transport: TransportContainer | null = null;
  hasConsumptionDataApproval = false;
  hasDirectReceptionApproval = false;
  note: string | null = null;
  slaNotes: Array<DossierSlaNote> = [];
  slaNotesTypes: Array<SlaNoteType> = [];
  isCarwashProhibited = false;
  keyCode: QrCode | null = null;
  hasSurveys = false;
  hasChecklists = false;
  hasTestDrives = false;
  hasTires = false;
  hasFluids = false;
  hasFluidReplenishmentApproval = false;
  fluidLevelsChecked = false;
  minutesPerTimeUnit: number | null = null;
  isVisible = false;
  stacks: Array<DossierStack> = [];
  partsPickingLocation: string | null = null;
  followUpOn: Moment | null = null;
  communicationPreference = '';

  getAppointmentState(): AppointmentState {
    if (this.waitsOnPremise) {
      return AppointmentState.WAITS_ON_PREMISE;
    } else if (this.preferredPickUpOn || this.transport?.delivery) {
      return AppointmentState.PICKUP;
    } else {
      return AppointmentState.NORMAL;
    }
  }

  updateAppointment(data: AppointmentData, user: User): void {
    this.waitsOnPremise = data.waitsOnPremise;
    this.mobilitySolution = data.mobilitySolution;
    // NOTE in case of delivery by customer, appointmentOn can't be changed
    // NOTE preferredPickUpOn refers to the customer picking up the car from the dealership
    // (not to be confused with pickup service, which is the dealer picking up the car from the customer)
    this.preferredPickUpOn = data.transportOut.byCustomer ? data.transportOut?.time : null;
    if (!this.transport) this.transport = new TransportContainer();
    this.transport.update(data.transportIn, data.transportOut, user);
  }

  updateSlaNotes(notes: Array<DossierSlaNote>) {
    this.slaNotes = notes;
  }

  getChatId(type: ChatType): ChatId | null {
    return visitChatType(type, {
      internal: () => this.internalChatId,
      external: () => this.externalChatId,
    });
  }

  setChatId(type: ChatType, chatId: ChatId): void {
    visitChatType(type, {
      internal: () => this.internalChatId = chatId,
      external: () => this.externalChatId = chatId,
    });
  }

  updateWith(that: Dossier): void {
    this.preferredPickUpOn = that.preferredPickUpOn;
    this.isInvoiced = that.isInvoiced;
    this.waitsOnPremise = that.waitsOnPremise;
    this.isLeasing = that.isLeasing;
    this.warrantyStatus = that.warrantyStatus;
    this.note = that.note;
    this.workshopTeamId = that.workshopTeamId;
    this.exteriorCleaningStatus = that.exteriorCleaningStatus;
    this.interiorCleaningStatus = that.interiorCleaningStatus;
    this.isVisible = that.isVisible;
    this.hasFluidReplenishmentApproval = that.hasFluidReplenishmentApproval;
  }
}